import {
  Component,
  ChangeDetectorRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Optional,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { PosOperationsService, PosProductVariant } from '../../../apps/point-of-sale/services/pos-operations.service';

import { InventoryService } from '../../../core/services/inventory.service';
import {
  InventoryProduct,
  InventoryProductAttributeLine,
  InventoryProductAttributeValueOption,
  InventoryProductVariant,
} from '../../../apps/inventory/products/products/products.type';

interface VariantSelectionOptions {
  emitForm?: boolean;
  emitOutputs?: boolean;
}

@Component({
  selector: 'app-product-variant-picker',
  standalone: false,
  templateUrl: './product-variant-picker.html',
  styleUrl: './product-variant-picker.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProductVariantPicker),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ProductVariantPicker),
      multi: true,
    },
  ],
})
export class ProductVariantPicker implements OnChanges, OnDestroy, ControlValueAccessor, Validator {
  @Input() product: InventoryProduct | null = null;
  @Input() productId: string | null = null;
  /** POS reads use the cashier-authorized catalogue, not unrestricted Inventory. */
  @Input() terminalId: string | null = null;
  @Input() selectedValueIds: string[] = [];
  @Input() selectedVariantId: string | null = null;
  @Input() disabled = false;
  @Input() required = false;
  @Input() triggerClass = 'form-select';

  @Output() selectedValueIdsChange = new EventEmitter<string[]>();
  @Output() variantSelected = new EventEmitter<InventoryProductVariant | null>();
  @Output() selectedVariantIdChange = new EventEmitter<string | null>();

  posVariants: PosProductVariant[] = [];

  open = false;
  touched = false;
  preloadPending = false;
  preloadError = '';

  private internalDisabled = false;
  private controlValue: string | null = null;
  private preloadedProduct: InventoryProduct | null = null;
  private preloadKey = '';
  private preloadRequestId = 0;
  private validatorChangeQueued = false;
  private destroyed = false;
  private formControl: AbstractControl | null = null;

  constructor(private readonly inventoryService: InventoryService,
    @Optional() private readonly posOperations: PosOperationsService | null = null,
    @Optional() private readonly cdr: ChangeDetectorRef | null = null) {}

  get hasSource(): boolean { return !!this.resolvedProduct || !!this.terminalId; }

  choosePosVariant(variant: PosProductVariant): void {
    if (this.isDisabled || this.preloadPending || !this.posVariants.some(row => row.id === variant.id)) return;
    this.markTouched();
    this.controlValue = this.selectedVariantId = variant.id;
    this.preloadError = '';
    this.onChange(variant.id);
    this.selectedVariantIdChange.emit(variant.id);
    this.open = false;
    this.notifyValidatorChange();
  }

  chooseVariant(variant: InventoryProductVariant): void {
    if (this.isDisabled || this.preloadPending || !this.resolvedProduct?.variants.some(row => row.id === variant.id)) return;
    this.markTouched();
    this.setVariant(variant, variant.attribute_value_ids ?? []);
    this.open = false;
  }

  get resolvedProduct(): InventoryProduct | null {
    return this.product ?? this.preloadedProduct;
  }

  get attributeLines(): InventoryProductAttributeLine[] {
    return this.resolvedProduct?.attribute_lines ?? [];
  }

  get hasAttributes(): boolean {
    return this.attributeLines.length > 0;
  }

  get visible(): boolean {
    return !!this.resolvedProduct || !!this.normalizedProductId() || !!this.currentVariantId();
  }

  get isDisabled(): boolean {
    return this.disabled || this.internalDisabled;
  }

  get selectedVariant(): InventoryProductVariant | null {
    const product = this.resolvedProduct;
    const variantId = this.currentVariantId();
    if (!product || !variantId) {
      return null;
    }

    return product.variants.find((variant: InventoryProductVariant) => variant.id === variantId) ?? null;
  }

  get buttonLabel(): string {
    if (this.preloadPending) {
      return 'Loading variant...';
    }

    if (this.terminalId) {
      return this.posVariants.find(row => row.id === this.currentVariantId())?.name
        || (this.posVariants.length ? 'Choose product variant' : 'No sale variants available');
    }

    const product = this.resolvedProduct;
    if (!product) {
      return 'Select product first';
    }

    const selected = this.selectedVariant;
    if (selected) {
      return this.variantLabel(selected);
    }

    if (!this.hasAttributes && product.variants.length === 1) {
      return this.variantLabel(product.variants[0]);
    }

    if (product.variants.length === 0) {
      return 'No variants available';
    }

    return 'Pick attributes';
  }

  get selectedSummaries(): Array<{
    attributeName: string;
    displayType: string | null | undefined;
    value: InventoryProductAttributeValueOption;
  }> {
    return this.attributeLines.flatMap((line) =>
      line.selected_values
        .filter((value: InventoryProductAttributeValueOption) => this.selectedValueIds.includes(value.id))
        .map((value) => ({
          attributeName: line.attribute_name,
          displayType: line.display_type,
          value,
        }))
    );
  }

  get missingAttributeCount(): number {
    return this.attributeLines.filter((line) => this.selectedCountForLine(line) !== 1).length;
  }

  get hasValidSelection(): boolean {
    if (this.preloadPending || this.preloadError) {
      return false;
    }

    if (this.terminalId) return this.posVariants.some(row => row.id === this.currentVariantId());

    const product = this.resolvedProduct;
    if (!product) {
      return !this.required && !this.currentVariantId();
    }

    if (!this.hasAttributes) {
      return product.variants.length === 1 || !!this.selectedVariant;
    }

    return this.missingAttributeCount === 0 && !!this.selectedVariant;
  }

  get showSelectionError(): boolean {
    return (this.touched || this.formControl?.touched === true)
      && !this.hasValidSelection
      && !this.preloadPending;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['terminalId'] && !this.terminalId) {
      this.cancelPreload();
      this.posVariants = [];
      this.preloadError = '';
    }
    if (this.terminalId) {
      if (changes['selectedVariantId']) this.controlValue = this.normalizeId(this.selectedVariantId);
      if (changes['terminalId'] || changes['productId']) void this.loadPosVariants();
      this.notifyValidatorChange();
      return;
    }
    if (changes['selectedValueIds']) {
      this.selectedValueIds = this.normalizeValueIds(this.selectedValueIds);
    }

    if (changes['selectedVariantId']) {
      const nextVariantId = this.normalizeId(this.selectedVariantId);
      if (nextVariantId !== this.currentVariantId()) {
        this.cancelPreload();
      }
      this.controlValue = nextVariantId;
    }

    if (changes['product']) {
      this.cancelPreload();
      this.open = false;
      if (this.product) {
        this.preloadedProduct = null;
      }
      this.preloadKey = '';
    }

    if (changes['productId'] && !this.product) {
      this.cancelPreload();
      this.preloadedProduct = null;
      this.preloadKey = '';
    }

    this.resolveCurrentSelection({ emitForm: false, emitOutputs: false });
    this.notifyValidatorChange();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.cancelPreload();
  }

  toggle(): void {
    if (this.isDisabled || this.preloadPending || !this.hasSource) {
      return;
    }

    this.open = !this.open;
    this.markTouched();
  }

  isSelected(valueId: string): boolean {
    return this.selectedValueIds.includes(valueId);
  }

  chooseValue(attributeLine: InventoryProductAttributeLine, value: InventoryProductAttributeValueOption): void {
    if (this.isDisabled || this.preloadPending) {
      return;
    }

    this.markTouched();
    const selected = new Set(this.selectedValueIds);
    for (const existingValue of attributeLine.selected_values) {
      selected.delete(existingValue.id);
    }
    selected.add(value.id);

    const selectedValueIds = Array.from(selected);
    this.selectedValueIds = selectedValueIds;
    const variant = this.findMatchingVariant(selectedValueIds);
    this.setVariant(variant, selectedValueIds);
    if (variant && this.missingAttributeCount === 0) {
      this.open = false;
    }
  }

  clear(): void {
    if (this.isDisabled) {
      return;
    }

    this.markTouched();
    this.setVariant(null, []);
    this.open = !!this.resolvedProduct;
  }

  writeValue(value: unknown): void {
    const nextValue = this.normalizeId(value);
    if (nextValue !== this.currentVariantId()) {
      this.cancelPreload();
    }

    this.controlValue = nextValue;
    this.selectedVariantId = this.controlValue;
    if (this.terminalId) { this.notifyValidatorChange(); return; }
    this.resolveCurrentSelection({ emitForm: false, emitOutputs: false });
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.internalDisabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    // Angular does not call ControlValueAccessor when a parent marks a control
    // as touched. Keep the control reference so a programmatic touch (for
    // example when adding the next document line) activates the picker error.
    this.formControl = control;
    if (this.preloadPending) {
      return {
        variantPreload: {
          message: 'Loading selected product variant.',
        },
      };
    }

    if (this.preloadError) {
      return {
        variantPreload: {
          message: this.preloadError,
        },
      };
    }

    if (!this.required && !this.resolvedProduct && !this.currentVariantId()) {
      return null;
    }

    if (this.hasValidSelection) {
      return null;
    }

    return {
      variantSelection: {
        message: this.terminalId ? 'Choose an available sale variant.' : 'Pick one value for every product attribute.',
      },
    };
  }

  private async loadPosVariants(): Promise<void> {
    this.cancelPreload();
    const requestId = this.preloadRequestId;
    this.posVariants = [];
    this.open = false;
    this.preloadPending = true;
    this.preloadError = '';
    try {
      if (!this.posOperations || !this.terminalId || !this.productId) throw new Error('Missing variant source');
      const products = await firstValueFrom(this.posOperations.registerProducts(this.terminalId));
      if (this.destroyed || this.preloadRequestId !== requestId) return;
      this.posVariants = products.find(row => row.id === this.productId)?.variants ?? [];
      if (this.currentVariantId() && !this.posVariants.some(row => row.id === this.currentVariantId())) {
        this.preloadError = 'The selected variant is unavailable for this terminal.';
      }
    } catch {
      if (this.preloadRequestId === requestId) this.preloadError = 'Unable to load sale variants. Reopen the product to retry.';
    } finally {
      if (this.preloadRequestId === requestId) {
        this.preloadPending = false;
        this.notifyValidatorChange();
        this.cdr?.markForCheck();
      }
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  valueLabel(value: InventoryProductAttributeValueOption): string {
    return value.value;
  }

  variantLabel(variant: InventoryProductVariant): string {
    const values = variant.values?.map((value: { value: string }) => value.value).filter(Boolean) ?? [];
    if (values.length > 0) {
      return values.join(' / ');
    }

    return variant.name || variant.sku || 'Default variant';
  }

  isColor(displayType: string | null | undefined): boolean {
    return displayType === 'color';
  }

  private resolveCurrentSelection(options: VariantSelectionOptions = {}): void {
    const variantId = this.currentVariantId();
    if (variantId) {
      this.resolvePatchedVariant(variantId, options);
      return;
    }

    this.cancelPreload();
    this.preloadError = '';
    this.syncProductDefault(options);
  }

  private resolvePatchedVariant(variantId: string, options: VariantSelectionOptions): void {
    const product = this.resolvedProduct;
    if (product) {
      const variant = product.variants.find((item: InventoryProductVariant) => item.id === variantId) ?? null;
      if (variant) {
        this.preloadError = '';
        this.syncValuesFromVariant(variantId, options);
        return;
      }

      this.preloadError = 'Unable to preload the selected product variant.';
      this.notifyValidatorChange();
      return;
    }

    const productId = this.normalizedProductId();
    if (productId) {
      void this.preloadVariantFromBackend(productId, variantId, options);
      return;
    }

    void this.preloadVariantFromBackend(null, variantId, options);
  }

  private async preloadVariantFromBackend(
    productId: string | null,
    variantId: string,
    options: VariantSelectionOptions
  ): Promise<void> {
    const key = `${productId || 'variant'}:${variantId}`;
    if (this.preloadPending && this.preloadKey === key) {
      return;
    }

    const requestId = ++this.preloadRequestId;
    this.preloadKey = key;
    this.preloadPending = true;
    this.preloadError = '';
    this.notifyValidatorChange();

    try {
      const product = await this.loadProductForVariant(productId, variantId);
      if (this.preloadRequestId !== requestId || this.preloadKey !== key) {
        return;
      }

      const variant = product?.variants.find((item: InventoryProductVariant) => item.id === variantId) ?? null;
      if (!product || !variant) {
        this.preloadedProduct = product;
        this.preloadError = 'Unable to preload the selected product variant.';
        return;
      }

      this.preloadedProduct = product;
      this.open = false;
      this.syncValuesFromVariant(variantId, options);
    } catch {
      if (this.preloadRequestId === requestId && this.preloadKey === key) {
        this.preloadError = 'Unable to preload the selected product variant.';
      }
    } finally {
      if (this.preloadRequestId === requestId && this.preloadKey === key) {
        this.preloadPending = false;
        this.notifyValidatorChange();
        this.cdr?.markForCheck();
      }
    }
  }

  private async loadProductForVariant(productId: string | null, variantId: string): Promise<InventoryProduct | null> {
    const normalizedProductId = this.normalizeId(productId);
    let product = normalizedProductId
      ? await this.inventoryService.getProductById(normalizedProductId)
      : null;

    if (product?.variants.some((variant: InventoryProductVariant) => variant.id === variantId)) {
      return product;
    }

    const productFromVariant = await this.inventoryService.getProductByVariantId(variantId);
    if (productFromVariant) {
      product = productFromVariant;
    }

    return product;
  }

  private setVariant(
    variant: InventoryProductVariant | null,
    valueIds: string[],
    options: VariantSelectionOptions = { emitForm: true, emitOutputs: true }
  ): void {
    const nextValueIds = this.normalizeValueIds(valueIds);
    this.preloadError = '';
    this.selectedValueIds = nextValueIds;
    this.controlValue = variant?.id ?? null;
    this.selectedVariantId = this.controlValue;

    if (options.emitOutputs !== false) {
      this.selectedValueIdsChange.emit(nextValueIds);
      this.variantSelected.emit(variant);
      this.selectedVariantIdChange.emit(this.controlValue);
    }

    if (options.emitForm !== false) {
      this.onChange(this.controlValue);
    }

    this.notifyValidatorChange();
  }

  private syncProductDefault(options: VariantSelectionOptions = {}): void {
    const product = this.resolvedProduct;
    if (!product) {
      return;
    }

    const variantId = this.currentVariantId();
    if (variantId) {
      const variantExists = product.variants.some((variant: InventoryProductVariant) => variant.id === variantId);
      if (variantExists) {
        this.syncValuesFromVariant(variantId, options);
        return;
      }

      this.preloadError = 'Unable to preload the selected product variant.';
      this.notifyValidatorChange();
      return;
    }

    if (!this.hasAttributes && product.variants.length === 1) {
      const variant = product.variants[0];
      this.setVariant(variant, variant.attribute_value_ids ?? [], options);
      return;
    }

    this.preloadError = '';
    this.controlValue = null;
    this.selectedVariantId = null;
    this.selectedValueIds = this.filterValuesForProduct(product, this.selectedValueIds);
    this.notifyValidatorChange();
  }

  private syncValuesFromVariant(variantId: string, options: VariantSelectionOptions = {}): void {
    const product = this.resolvedProduct;
    if (!product) {
      return;
    }

    const variant = product.variants.find((item) => item.id === variantId) ?? null;
    if (!variant) {
      return;
    }

    this.setVariant(variant, variant.attribute_value_ids ?? [], options);
  }

  private findMatchingVariant(selectedValueIds: string[]): InventoryProductVariant | null {
    const product = this.resolvedProduct;
    if (!product) {
      return null;
    }

    const selected = selectedValueIds.slice().sort().join('|');
    if (!selected && product.variants.length === 1) {
      return product.variants[0];
    }

    return product.variants.find((variant: InventoryProductVariant) => {
      const variantValues = (variant.attribute_value_ids ?? []).slice().sort().join('|');
      return variantValues === selected;
    }) ?? null;
  }

  private selectedCountForLine(line: InventoryProductAttributeLine): number {
    const selected = new Set(this.selectedValueIds);
    return line.selected_values.filter((value: InventoryProductAttributeValueOption) => selected.has(value.id)).length;
  }

  private normalizeValueIds(value: unknown): string[] {
    return Array.isArray(value)
      ? value.map((item) => String(item || '').trim()).filter(Boolean)
      : [];
  }

  private normalizeId(value: unknown): string | null {
    const normalized = String(value ?? '').trim();
    return normalized.length > 0 ? normalized : null;
  }

  private filterValuesForProduct(product: InventoryProduct, valueIds: string[]): string[] {
    const validValueIds = new Set(
      product.attribute_lines.flatMap((line) => line.selected_values.map((value) => value.id))
    );

    return this.normalizeValueIds(valueIds).filter((valueId) => validValueIds.has(valueId));
  }

  private normalizedProductId(): string {
    return String(this.productId ?? this.product?.id ?? this.preloadedProduct?.id ?? '').trim();
  }

  private currentVariantId(): string | null {
    return this.normalizeId(this.selectedVariantId) || this.controlValue;
  }

  private markTouched(): void {
    this.touched = true;
    this.onTouched();
  }

  private cancelPreload(): void {
    this.preloadRequestId++;
    this.preloadKey = '';
    this.preloadPending = false;
  }

  private notifyValidatorChange(): void {
    if (this.validatorChangeQueued || this.destroyed) {
      return;
    }

    this.validatorChangeQueued = true;
    queueMicrotask(() => {
      this.validatorChangeQueued = false;
      if (!this.destroyed) {
        this.onValidatorChange();
      }
    });
  }

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};
}
